import { Fragment, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchMailboxConfig, updateMailboxConfig, fetchMailboxMessages, importMailboxMessage } from '../api/mailbox';
import type { MailboxMessage } from '../types';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MailboxIngestion() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  // Mailbox configuration
  const [mailboxAddress, setMailboxAddress] = useState('');
  const [editingAddress, setEditingAddress] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  const [messages, setMessages] = useState<MailboxMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkImporting, setBulkImporting] = useState(false);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchMailboxMessages({ unread_only: unreadOnly });
      setMessages(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('mailbox.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [unreadOnly, t]);

  // Load mailbox config on mount
  useEffect(() => {
    fetchMailboxConfig()
      .then((cfg) => {
        setMailboxAddress(cfg.mailbox_address);
        setEditingAddress(cfg.mailbox_address);
        setConfigLoaded(true);
      })
      .catch(() => {
        setConfigLoaded(true); // proceed even if config fetch fails
      });
  }, []);

  // Load messages once config is loaded
  useEffect(() => {
    if (configLoaded) {
      loadMessages();
    }
  }, [configLoaded, loadMessages]);

  async function handleSaveConfig() {
    const trimmed = editingAddress.trim();
    if (!trimmed) {
      setConfigError(t('mailbox.mailboxRequired'));
      return;
    }
    setSavingConfig(true);
    setConfigError(null);
    try {
      const updated = await updateMailboxConfig({ mailbox_address: trimmed });
      setMailboxAddress(updated.mailbox_address);
      setEditingAddress(updated.mailbox_address);
      setShowConfig(false);
      loadMessages();
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : t('mailbox.configFailed'));
    } finally {
      setSavingConfig(false);
    }
  }

  async function handleImport(msg: MailboxMessage) {
    if (!window.confirm(t('mailbox.confirmImport', { subject: msg.subject }))) return;
    setImportingId(msg.id);
    setImportSuccess(null);
    try {
      const result = await importMailboxMessage(msg.id);
      setImportSuccess(t('mailbox.tenderCreated', { title: result.title }));
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(msg.id);
        return next;
      });
      setTimeout(() => navigate(`/tenders/${result.tender_id}/matches`, { state: { justCreated: true } }), 1500);
    } catch (err) {
      alert(err instanceof Error ? err.message : t('mailbox.importFailed'));
    } finally {
      setImportingId(null);
    }
  }

  async function handleBulkImport() {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(t('mailbox.confirmBulkImport', { count }))) return;

    setBulkImporting(true);
    setImportSuccess(null);
    const results: string[] = [];
    const failed: string[] = [];

    for (const id of selectedIds) {
      try {
        const result = await importMailboxMessage(id);
        results.push(result.title);
      } catch {
        const msg = messages.find((m) => m.id === id);
        failed.push(msg?.subject ?? id);
      }
    }

    const importedIds = new Set(
      messages
        .filter((m) => selectedIds.has(m.id) && !failed.includes(m.subject))
        .map((m) => m.id)
    );
    setMessages((prev) => prev.filter((m) => !importedIds.has(m.id)));
    setSelectedIds(new Set());
    setBulkImporting(false);

    if (results.length > 0) {
      setImportSuccess(t('mailbox.tendersCreated', { count: results.length }));
    }
    if (failed.length > 0) {
      alert(t('mailbox.bulkImportFailed', { count: failed.length, subjects: failed.join(', ') }));
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === messages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(messages.map((m) => m.id)));
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('mailbox.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('mailbox.subtitle')}
          </p>
        </div>
      </header>

      {/* Mailbox configuration */}
      <section aria-label={t('mailbox.mailboxConfig')} className="card space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-700">{t('mailbox.sharedMailbox')}</h2>
            {mailboxAddress && !showConfig && (
              <span className="text-sm text-gray-600">{mailboxAddress}</span>
            )}
            {!mailboxAddress && !showConfig && (
              <span className="text-sm text-gray-400 italic">{t('mailbox.notConfigured')}</span>
            )}
          </div>
          {!showConfig && (
            <button
              onClick={() => { setShowConfig(true); setEditingAddress(mailboxAddress); setConfigError(null); }}
              className="text-xs text-brand-600 hover:text-brand-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
            >
              {mailboxAddress ? t('mailbox.change') : t('mailbox.configure')}
            </button>
          )}
        </div>

        {showConfig && (
          <div className="space-y-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="mailbox-address" className="form-label">
                {t('mailbox.mailboxLabel')}
              </label>
              <p id="mailbox-address-hint" className="text-xs text-gray-400">
                {t('mailbox.mailboxHint')}
              </p>
              <input
                id="mailbox-address"
                type="email"
                value={editingAddress}
                onChange={(e) => { setEditingAddress(e.target.value); setConfigError(null); }}
                placeholder="tenders@yourcompany.com"
                aria-describedby="mailbox-address-hint"
                className="form-input max-w-md"
              />
            </div>

            {configError && (
              <p role="alert" className="text-sm text-red-600">{configError}</p>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className="btn-primary"
                aria-busy={savingConfig}
              >
                {savingConfig ? t('mailbox.saving') : t('common.save')}
              </button>
              <button
                onClick={() => { setShowConfig(false); setEditingAddress(mailboxAddress); setConfigError(null); }}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Filters & actions */}
      <section aria-label={t('mailbox.filters')} className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          {t('mailbox.unreadOnly')}
        </label>
        <button onClick={loadMessages} className="btn-secondary" aria-label={t('mailbox.refreshMailbox')}>
          {t('common.refresh')}
        </button>
        {selectedIds.size > 0 && (
          <button
            onClick={handleBulkImport}
            disabled={bulkImporting}
            className="btn-primary"
            aria-busy={bulkImporting}
          >
            {bulkImporting
              ? t('mailbox.importingMsg')
              : t('mailbox.importSelected', { count: selectedIds.size })}
          </button>
        )}
      </section>

      {/* Success banner */}
      {importSuccess && (
        <div role="status" aria-live="polite" className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-700">
          {importSuccess}
        </div>
      )}

      {/* Error alert */}
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
          <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">&#x27F3;</span>
          {t('mailbox.loading')}
        </div>
      )}

      {/* Message list */}
      {!loading && !error && (
        <>
          {messages.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-500 text-sm">{t('mailbox.noMessages')}</p>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="data-table" aria-label={t('mailbox.messageTable')}>
                  <thead>
                    <tr>
                      <th scope="col" className="w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === messages.length && messages.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          aria-label={t('mailbox.selectAll')}
                        />
                      </th>
                      <th scope="col">{t('mailbox.subject')}</th>
                      <th scope="col">{t('mailbox.from')}</th>
                      <th scope="col">{t('mailbox.received')}</th>
                      <th scope="col">{t('mailbox.attachments')}</th>
                      <th scope="col"><span className="sr-only">{t('common.actions')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg) => (
                      <Fragment key={msg.id}>
                        <tr className={!msg.is_read ? 'bg-blue-50/40' : undefined}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(msg.id)}
                              onChange={() => toggleSelect(msg.id)}
                              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                              aria-label={t('mailbox.selectMsg', { subject: msg.subject })}
                            />
                          </td>
                          <td className="max-w-xs">
                            <button
                              onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                              className={[
                                'text-left hover:text-brand-700 focus:outline-none focus-visible:underline truncate block max-w-full',
                                !msg.is_read ? 'font-semibold text-gray-900' : 'text-gray-700',
                              ].join(' ')}
                              aria-expanded={expandedId === msg.id}
                              aria-label={t('mailbox.togglePreview', { subject: msg.subject })}
                            >
                              {msg.subject || t('mailbox.noSubject')}
                            </button>
                          </td>
                          <td className="text-gray-600 text-sm">{msg.from}</td>
                          <td className="text-sm">
                            <time dateTime={msg.received_at}>
                              {new Date(msg.received_at).toLocaleDateString(dateLocale, {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })}
                              {' '}
                              <span className="text-gray-400">
                                {new Date(msg.received_at).toLocaleTimeString(dateLocale, {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </time>
                          </td>
                          <td className="text-sm text-gray-500">
                            {msg.has_attachments ? (
                              <span className="inline-flex items-center gap-1">
                                <span aria-hidden="true">&#x1F4CE;</span>
                                {msg.attachments.length}
                              </span>
                            ) : (
                              <span className="text-gray-400">&#x2014;</span>
                            )}
                          </td>
                          <td>
                            <button
                              onClick={() => handleImport(msg)}
                              disabled={importingId === msg.id || bulkImporting}
                              className="text-xs text-brand-600 hover:text-brand-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
                              aria-label={t('mailbox.importAsLabel', { subject: msg.subject })}
                            >
                              {importingId === msg.id ? t('mailbox.importingMsg') : t('mailbox.importAsTender')}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded preview row */}
                        {expandedId === msg.id && (
                          <tr key={`${msg.id}-preview`} className="bg-gray-50">
                            <td colSpan={6} className="px-6 py-4">
                              <div className="space-y-3">
                                <div>
                                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('mailbox.preview')}</h3>
                                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                                    {msg.body_preview || t('mailbox.noTextContent')}
                                  </p>
                                </div>

                                {msg.has_attachments && msg.attachments.length > 0 && (
                                  <div>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('mailbox.attachments')}</h3>
                                    <ul className="space-y-1">
                                      {msg.attachments.map((att) => (
                                        <li key={att.id} className="flex items-center gap-2 text-sm text-gray-600">
                                          <span aria-hidden="true">&#x1F4CE;</span>
                                          <span className="font-medium">{att.name}</span>
                                          <span className="text-gray-400">({formatFileSize(att.size)})</span>
                                          <span className="text-gray-400">{att.content_type}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                <div className="flex items-center gap-2 pt-1">
                                  <span className="text-xs text-gray-400">
                                    {t('mailbox.fromReceived', {
                                      from: msg.from,
                                      date: new Date(msg.received_at).toLocaleString(dateLocale),
                                    })}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
